// Pipeline de modificadores de combate.
//
// Um combate passa por três estágios, nesta ordem, e cada modificador pode atuar
// em qualquer um deles:
//   1. dice  — ajusta quantos dados cada lado rola;
//   2. rules — altera regras de comparação (empate, vitória automática);
//   3. rolls — altera valores ou rerrola depois da rolagem;
//   4. outcome — ajusta as perdas calculadas (ex.: Stone Skin).
// Vocações e loot já entram por aqui; add-ons futuros (skull, level do exército,
// templos...) só precisam registrar novos modificadores.

import type { Rng } from '../rng';
import type { AttackRoute, CombatNote, GameState, PlayerId, TerritoryId } from '../types';

export interface CombatContext {
  /** Estado em rascunho (pode ser lido; modificadores só alteram o contexto ou flags do turno). */
  readonly state: GameState;
  kind: 'territory' | 'boss';
  attacker: PlayerId;
  /** Dono do território defensor; null quando o alvo é um boss. */
  defender: PlayerId | null;
  from: TerritoryId;
  to: TerritoryId | null;
  route: AttackRoute | 'lair';
  sea: boolean;
  attackDice: number;
  defenseDice: number;
  attackRolls: number[];
  defenseRolls: number[];
  /** Quem vence um empate. */
  tieGoesTo: 'attacker' | 'defender';
  /** O defensor vence todas as comparações. */
  defenderWinsAll: boolean;
  notes: CombatNote[];
}

export type RollStage = 'initial' | 'reroll';

export interface CombatModifier {
  id: string;
  dice?(ctx: CombatContext): void;
  rolls?(ctx: CombatContext, rng: Rng, stage: RollStage): void;
  rules?(ctx: CombatContext): void;
  outcome?(ctx: CombatContext, out: CombatOutcome): void;
}

export interface CombatOutcome {
  attackerLoss: number;
  defenderLoss: number;
}

export const sortDesc = (xs: number[]) => xs.slice().sort((a, b) => b - a);

export function applyDiceStage(ctx: CombatContext, modifiers: CombatModifier[]): void {
  for (const m of modifiers) m.dice?.(ctx);
  ctx.attackDice = Math.max(0, ctx.attackDice);
  ctx.defenseDice = Math.max(0, ctx.defenseDice);
}

export function rollAttack(ctx: CombatContext, rng: Rng): void {
  ctx.attackRolls = sortDesc(rng.dice(ctx.attackDice));
}

export function rollDefense(ctx: CombatContext, rng: Rng): void {
  ctx.defenseRolls = sortDesc(rng.dice(ctx.defenseDice));
}

export function applyRollStage(ctx: CombatContext, rng: Rng, stage: RollStage, modifiers: CombatModifier[]): void {
  for (const m of modifiers) {
    m.rolls?.(ctx, rng, stage);
    ctx.attackRolls = sortDesc(ctx.attackRolls);
    ctx.defenseRolls = sortDesc(ctx.defenseRolls);
  }
}

export function applyRulesStage(ctx: CombatContext, modifiers: CombatModifier[]): void {
  for (const m of modifiers) m.rules?.(ctx);
}

/** Compara par a par, do maior para o menor. */
export function compareRolls(
  attackRolls: number[],
  defenseRolls: number[],
  tieGoesTo: 'attacker' | 'defender' = 'defender',
  defenderWinsAll = false,
): CombatOutcome {
  const a = sortDesc(attackRolls);
  const d = sortDesc(defenseRolls);
  const pairs = Math.min(a.length, d.length);
  let attackerLoss = 0;
  let defenderLoss = 0;
  for (let i = 0; i < pairs; i++) {
    if (defenderWinsAll) attackerLoss++;
    else if (a[i] > d[i]) defenderLoss++;
    else if (a[i] < d[i]) attackerLoss++;
    else if (tieGoesTo === 'defender') attackerLoss++;
    else defenderLoss++;
  }
  return { attackerLoss, defenderLoss };
}

export function outcomeOf(ctx: CombatContext): CombatOutcome {
  return compareRolls(ctx.attackRolls, ctx.defenseRolls, ctx.tieGoesTo, ctx.defenderWinsAll);
}

/** Perdas finais: comparação dos dados + estágio `outcome` dos modificadores. */
export function finalOutcome(ctx: CombatContext, modifiers: CombatModifier[]): CombatOutcome {
  const out = outcomeOf(ctx);
  for (const m of modifiers) m.outcome?.(ctx, out);
  return out;
}
